import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "batchplay"
    }}>{`batchPlay`}</h1>
    <p>{`At the heart of our APIs is `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{`, a method that can send multiple actions into Photoshop event queue, and return you their results. `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{` is the evolution of `}<inlineCode parentName="p">{`executeAction`}</inlineCode>{` from ExtendScript. Where `}<inlineCode parentName="p">{`executeAction`}</inlineCode>{` could only play one descriptor at a time, `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{` accepts an array of action descriptors. `}</p>
    <h2 {...{
      "id": "api"
    }}>{`API`}</h2>
    <p>{`BatchPlay can directly be accessed from the Photoshop action module, and by default is asynchronous`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`require('photoshop').action.batchPlay(descriptors: ActionDescriptor[], options: Object): Promise<Object[]>
`}</code></pre>
    <p>{`Below we will dive into both how action descriptors are defined in JavaScript, and what options are available.`}</p>
    <h3 {...{
      "id": "action-descriptors"
    }}>{`Action Descriptors`}</h3>
    <p>{`In ExtendScript, we provided a class around constructing descriptors, references, and putting values in. With `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{`, we have added a way to construct ActionDescriptors directly from JS objects. This is sometimes referred to as `}<inlineCode parentName="p">{`actionJSON`}</inlineCode>{`, as well. `}</p>
    <p>{`Here's an example of a simple ActionDescriptor defined in JSON:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`{
  _obj: "colorSampler", // _obj is a reserved identifier for the event of the action descriptor. 
                  // _obj is universally needed for all descriptors being passed into batchPlay.
  _target: { // This is the ActionReference, the element on which this action should be played
    _ref: "document", // In this case we are trying to play an event on the document element
    _enum: "ordinal", // _enum and _value signify that this is the active element. In document's case, this is the active document
    _value: "targetEnum"
  },
  samplePoint: { // All other fields are optional and are used by the action itself
    horizontal: 100,
    vertical: 100
  }
}
`}</code></pre>
    <h4 {...{
      "id": "string-identifiers"
    }}>{`String identifiers`}</h4>
    <p>{`If you have used `}<inlineCode parentName="p">{`executeAction`}</inlineCode>{` in ExtendScript, you are probably familiar with 4 character codes (OSTypes) and helper methods around them. In JavaScript, we support string identifiers. However, you can still use an OSType by prepending it with a `}<inlineCode parentName="p">{`$`}</inlineCode>{` sign and passing that as a string, like `}<inlineCode parentName="p">{`'$app `}</inlineCode>{` (mind the space!).`}</p>
    <h4 {...{
      "id": "accepted-action-descriptors"
    }}>{`Accepted action descriptors`}</h4>
    <ul>
      <li parentName="ul">{`One way of gaining insight about what Photoshop accepts as acceptable action descriptors would be to add a listener via the `}<inlineCode parentName="li">{`app.eventNotifier`}</inlineCode>{` property, and then performing the action. Use the listener output to construct your action descriptors.`}</li>
    </ul>
    <h3 {...{
      "id": "batchplay-options"
    }}>{`BatchPlay options`}</h3>
    <p>{`The second argument of batchPlay adjusts the options. Below is the list of options that are most commonly needed.`}</p>
    <h4 {...{
      "id": "synchronousexecution-default-false"
    }}>{`synchronousExecution (default: false)`}</h4>
    <p>{`If set to true, batchPlay will block the entire scripting thread until it resolves, then return the value(s). We use this in the DOM API for property getters and setters, as it allows for simpler code. For expensive operations, make sure you keep this false.`}</p>
    <h4 {...{
      "id": "modalbehavior-default-fail"
    }}>{`modalBehavior (default: "fail")`}</h4>
    <p>{`This can be "wait", "execute" or "fail", and describes what the batchPlay command should do if Photoshop is modal. Keep in mind that if you are showing a dialog via your own plug-in, that is also considered a modal state.`}</p>
    <h4 {...{
      "id": "historystateinfo-default-none"
    }}>{`historyStateInfo (default: none)`}</h4>
    <p>{`Request Photoshop to describe the entire batchPlayed series of actions as a single history state. History state should have two properties:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`name`}</inlineCode>{`: A string to show the name of the history event in History panel`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`target`}</inlineCode>{`: A document reference for where to create the history state at. Keep in mind that if you point at a different document, or if your batchPlays aren't all acting on the same document, the behavior is undefined.`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      